import type { DefectKind } from './defects.js';

/**
 * What each defect means, why a producer causes it, and what to change.
 *
 * **This catalogue is the export-settings deliverable.** AIONA asked for
 * recommended vector-PDF export settings for AutoCAD and SolidWorks, and the
 * honest form of that document is not a separate essay written once and left to
 * rot: it is the same sentences the tool says when it finds the defect. One
 * source, so the advice on the screen and the advice in the document cannot
 * drift, and so a defect nobody wrote guidance for is a build failure rather
 * than a blank space on a page.
 *
 * **Confidence, stated because it varies.** The *causes* are measured — every
 * one of them is a signal this pipeline reads out of real files. The *setting
 * names* are not: they come from the applications' documented options, they
 * differ between releases, and AIONA's customers run Japanese interfaces where
 * the menu labels are translated. Each setting is therefore described by what
 * it does as well as what it is called, and the labels want confirming against
 * the versions AIONA's customers actually run before this goes out. That check
 * is one afternoon with the two applications open and it is the last thing
 * missing from this document.
 */

export interface Bilingual {
  en: string;
  ja: string;
}

export interface Guidance {
  title: Bilingual;
  /** Why a producer writes a file this way. Never numbers — those are on the defect. */
  cause: Bilingual;
  /** What to change, in terms anyone can act on without knowing an application. */
  recommendation: Bilingual;
  /** The setting, where the application has one. Null where re-exporting cannot help. */
  autocad: Bilingual | null;
  solidworks: Bilingual | null;
}

export const DEFECT_KINDS = [
  'encrypted',
  'not_a_pdf',
  'corrupt',
  'too_large',
  'timed_out',
  'raster_only',
  'no_content',
  'raster_content',
  'text_as_outlines',
  'text_layer_over_scan',
  'unsupported_curves',
  'font_not_embedded',
  'non_standard_sheet',
  'content_outside_page',
  'text_write_order',
] as const satisfies readonly DefectKind[];

export const GUIDANCE: Record<DefectKind, Guidance> = {
  encrypted: {
    title: { en: 'The file is protected', ja: 'ファイルが保護されています' },
    cause: {
      en: 'A password or a permissions restriction was applied when the PDF was saved. The drawing cannot be opened at all, so nothing has been read from it.',
      ja: 'PDF の保存時にパスワードまたは権限の制限が設定されています。ファイルを開くことができないため、図面の内容は一切読み取られていません。',
    },
    recommendation: {
      en: 'Export the drawing again with no security settings applied. Protection can be added afterwards, to a copy, without affecting the conversion.',
      ja: 'セキュリティ設定を付けずに再出力してください。変換後の控えに保護をかけることは、変換結果に影響しません。',
    },
    autocad: {
      en: 'In the plot dialog, PDF Options: leave the password and permissions fields empty.',
      ja: '印刷ダイアログの「PDF オプション」で、パスワードと権限の欄を空のままにします。',
    },
    solidworks: {
      en: 'Save As → PDF → Options: leave the security options unset.',
      ja: '「指定保存」→ PDF →「オプション」で、セキュリティ設定を無効にします。',
    },
  },

  not_a_pdf: {
    title: { en: 'This is not a PDF', ja: 'PDF ではありません' },
    cause: {
      en: 'The file does not carry a PDF header. A DWG, a DXF, an image or a renamed file will all look like this.',
      ja: 'ファイルに PDF のヘッダーがありません。DWG・DXF・画像ファイル、あるいは拡張子だけを変更したファイルが該当します。',
    },
    recommendation: {
      en: 'Send the vector PDF exported from the CAD application, not the CAD file itself.',
      ja: 'CAD ファイルそのものではなく、CAD から出力したベクター PDF をお送りください。',
    },
    autocad: {
      en: 'Plot to the DWG To PDF.pc3 plotter, or Export → PDF.',
      ja: '「DWG To PDF.pc3」プロッタで印刷するか、「書き出し」→ PDF を使用します。',
    },
    solidworks: {
      en: 'File → Save As → PDF, from the drawing document.',
      ja: '図面ドキュメントから「ファイル」→「指定保存」→ PDF を選択します。',
    },
  },

  corrupt: {
    title: { en: 'The file could not be read', ja: 'ファイルを読み取れません' },
    cause: {
      en: 'The PDF structure is damaged. This is usually an interrupted download, an incomplete copy, or a file assembled by a tool that wrote it incorrectly.',
      ja: 'PDF の構造が破損しています。ダウンロードの中断、コピーの失敗、または書き出しツールの不具合が主な原因です。',
    },
    recommendation: {
      en: 'Export and send the drawing again. If the second copy fails in the same way, the exporter itself is producing the damage.',
      ja: '再出力のうえ、あらためてお送りください。同じ症状が再現する場合は、出力ツール側に原因があります。',
    },
    autocad: null,
    solidworks: null,
  },

  too_large: {
    title: { en: 'The file is over the size limit', ja: 'ファイルサイズが上限を超えています' },
    cause: {
      en: 'The drawing is larger than this service accepts. Embedded raster images are the usual reason a vector drawing becomes large.',
      ja: '本サービスの受付上限を超えています。ベクター図面が大きくなる原因の多くは、埋め込まれたラスター画像です。',
    },
    recommendation: {
      en: 'Split the drawing into separate sheets, or remove embedded photographs and scans that are not part of the geometry.',
      ja: 'シートごとに分割するか、図形に関係しない写真・スキャン画像を取り除いてください。',
    },
    autocad: null,
    solidworks: null,
  },

  timed_out: {
    title: { en: 'Reading the drawing took too long', ja: '読み取りが時間内に完了しませんでした' },
    cause: {
      en: 'The drawing exceeded the time budget for extraction. Very large numbers of individual marks — hatch patterns exploded into lines, for instance — are the common reason.',
      ja: '抽出処理の制限時間を超えました。ハッチングが個別の線分に分解されている場合など、要素数が極端に多い図面で起こります。',
    },
    recommendation: {
      en: 'Split the drawing into sheets, or keep hatching and fills as patterns rather than exploding them before export.',
      ja: 'シートごとに分割するか、ハッチングや塗りつぶしを分解せずパターンのまま出力してください。',
    },
    autocad: null,
    solidworks: null,
  },

  raster_only: {
    title: { en: 'This page is a picture, not a drawing', ja: 'このページは画像であり図面ではありません' },
    cause: {
      en: 'The page carries an image and no vector geometry. Either the drawing was scanned, or it was printed to PDF through a driver that rasterises everything. A picture of a drawing carries no lines, arcs or text to convert — only pixels.',
      ja: 'ページに画像のみが含まれ、ベクター要素がありません。図面をスキャンした場合か、ラスター化する印刷ドライバーで PDF 出力した場合に該当します。図面の画像には、変換対象となる線分・円弧・文字が存在せず、画素しかありません。',
    },
    recommendation: {
      en: 'Export the drawing as vector PDF from the CAD application it was authored in. If only a scan exists, the drawing has to be recognised from the image instead, which is a different and less accurate process.',
      ja: '作図した CAD からベクター PDF として出力してください。スキャンしか存在しない場合は、画像からの認識処理となり、精度は大きく下がります。',
    },
    autocad: {
      en: 'Plot with DWG To PDF.pc3 and leave "Plot as raster" / raster output unselected. In the plotter Custom Properties, keep Vector Quality set rather than plotting the whole sheet as an image.',
      ja: '「DWG To PDF.pc3」で印刷し、ラスター出力の指定は行わないでください。プロッタの「カスタム プロパティ」では、シート全体を画像化せず、ベクター品質の設定を使用します。',
    },
    solidworks: {
      en: 'Save As → PDF rather than printing through a PDF image printer. Avoid "High quality shaded" views, which export as images.',
      ja: '画像化する PDF プリンターで印刷せず、「指定保存」→ PDF を使用してください。シェイディング表示の高品質出力は画像として書き出されるため避けてください。',
    },
  },

  no_content: {
    title: { en: 'This page is empty', ja: 'このページは空です' },
    cause: {
      en: 'The page carries no geometry and no image. A cover sheet, a separator page, or a plot area that captured nothing will all look like this.',
      ja: 'ページに図形も画像も含まれていません。表紙・区切りページ、あるいは何も含まない印刷範囲を指定した場合に該当します。',
    },
    recommendation: {
      en: 'Export only the sheets that carry drawings, or set the plot area to the drawing extents.',
      ja: '図面が描かれたシートのみを出力するか、印刷範囲を図面の範囲に設定してください。',
    },
    autocad: {
      en: 'In the plot dialog, set Plot area to Extents or Layout rather than Display or Window.',
      ja: '印刷ダイアログの「印刷範囲」を「表示画面」や「窓」ではなく「図面範囲」または「レイアウト」に設定します。',
    },
    solidworks: {
      en: 'Save As → PDF → Options: export the sheets that carry drawings rather than all sheets.',
      ja: '「指定保存」→ PDF →「オプション」で、全シートではなく図面のあるシートを指定して出力します。',
    },
  },

  raster_content: {
    title: { en: 'Part of this page is an image', ja: 'ページの一部が画像です' },
    cause: {
      en: 'An image sits on a page that is otherwise vector geometry — commonly a company logo, a photograph, or a scanned detail pasted into the sheet. Whatever the image contains is not read: extraction does not look inside pixels.',
      ja: 'ベクター図面のページに画像が配置されています。会社ロゴ、写真、貼り込まれたスキャン図などが該当します。画像の内側は読み取られません。抽出処理は画素の中を解析しないためです。',
    },
    recommendation: {
      en: 'No change is needed if the image is a logo. If it carries geometry that has to be converted, that geometry must exist as CAD entities in the source drawing.',
      ja: 'ロゴであれば対応は不要です。変換対象となる図形が画像内にある場合は、元図面に CAD 要素として作図されている必要があります。',
    },
    autocad: null,
    solidworks: null,
  },

  text_as_outlines: {
    title: { en: 'The text was written as line-work', ja: '文字が線分として出力されています' },
    cause: {
      en: 'The exporter converted every character into closed outlines, so what looks like text is a large number of small filled shapes. On Japanese drawings the usual reason is an SHX stroke font: it has no Unicode mapping, so AutoCAD cannot write it as text and draws it as geometry instead. Some exporters also do this deliberately, to guarantee appearance on machines that lack the font.',
      ja: '出力時に各文字が閉じた輪郭線に変換されており、文字に見えるものが多数の小さな図形になっています。日本語図面では SHX ストロークフォントが主な原因です。Unicode との対応を持たないため、AutoCAD は文字として書き出せず図形として描画します。フォントのない環境でも表示を保つため、意図的にこの設定を使う出力ツールもあります。',
    },
    recommendation: {
      en: 'Use a TrueType font — MS Gothic or Yu Gothic for Japanese — in place of an SHX font, and export with fonts embedded rather than converted to geometry. Characters cannot be recovered from outlines afterwards: this is the one defect where re-exporting is the only route.',
      ja: 'SHX フォントではなく TrueType フォント（日本語では MS ゴシックまたは游ゴシック）を使用し、文字を図形化せずフォントを埋め込んで出力してください。輪郭線から文字を復元することはできないため、再出力以外の解決手段はありません。',
    },
    autocad: {
      en: 'Plotter Custom Properties → Font Handling: choose "Capture fonts used in the drawing" rather than "Convert all text to geometry". Change SHX text styles to TrueType in the drawing first, since SHX text is written as geometry regardless.',
      ja: 'プロッタの「カスタム プロパティ」→「フォントの処理」で、「すべての文字を図形に変換」ではなく「図面で使用しているフォントを取り込む」を選択します。SHX の文字は設定にかかわらず図形として出力されるため、図面側で文字スタイルを TrueType に変更してください。',
    },
    solidworks: {
      en: 'Save As → PDF → Options: enable "Embed fonts". Avoid any "export text as geometry" or stroke-font option.',
      ja: '「指定保存」→ PDF →「オプション」で「フォントを埋め込む」を有効にします。文字を図形として書き出す設定やストロークフォントの指定は使用しないでください。',
    },
  },

  text_layer_over_scan: {
    title: { en: 'The text is a recognition layer', ja: '文字は認識レイヤーです' },
    cause: {
      en: 'Text is drawn in an invisible render mode, which is how recognition software stores what it read over a scanned image. The characters come from optical recognition rather than from the drawing, so they carry that process\'s errors and its placement rather than the author\'s.',
      ja: '文字が非表示の描画モードで配置されています。これはスキャン画像に対して文字認識ソフトが読み取り結果を重ねる際の形式です。文字は図面ではなく光学認識の結果であるため、認識誤りと認識側の配置をそのまま引き継ぎます。',
    },
    recommendation: {
      en: 'Export a vector PDF from the original CAD file if one exists. A recognition layer over a scan is not the drawing, and its accuracy is the recognition software\'s, not this pipeline\'s.',
      ja: '元の CAD ファイルが存在する場合は、そこからベクター PDF を出力してください。スキャンに重ねられた認識レイヤーは図面そのものではなく、精度は認識ソフト側に依存します。',
    },
    autocad: {
      en: 'Plot the original drawing to DWG To PDF.pc3 rather than sending a scanned copy.',
      ja: 'スキャンした控えではなく、元図面を「DWG To PDF.pc3」で印刷してください。',
    },
    solidworks: {
      en: 'Save As → PDF from the original drawing document rather than sending a scan.',
      ja: 'スキャンではなく、元の図面ドキュメントから「指定保存」→ PDF を実行してください。',
    },
  },

  unsupported_curves: {
    title: { en: 'Curves that are not arcs', ja: '円弧ではない曲線があります' },
    cause: {
      en: 'The drawing contains Bézier curves that do not describe a circular arc — splines, ellipses and freeform curves. Phase 1 converts lines, polylines, circles and arcs, and these are none of those. They are recorded rather than approximated: fitting a spline into short straight segments would be a guess that looks like a result.',
      ja: '円弧ではないベジェ曲線が含まれています。スプライン、楕円、自由曲線が該当します。フェーズ 1 の変換対象は線分・ポリライン・円・円弧であり、これらはいずれにも当てはまりません。近似はせず記録のみ行います。スプラインを短い直線で近似することは、結果に見える推測にすぎないためです。',
    },
    recommendation: {
      en: 'Nothing in the export settings changes this: a spline is a spline however it is written out. Where the shape is genuinely a circle or an arc, drawing it as one in the source rather than as a fitted curve makes it convertible.',
      ja: '出力設定では解決しません。スプラインはどのように書き出しても曲線のままです。本来円や円弧である形状については、近似曲線ではなく円・円弧として作図すれば変換対象になります。',
    },
    autocad: null,
    solidworks: null,
  },

  font_not_embedded: {
    title: { en: 'A font the file does not carry', ja: 'ファイルに含まれていないフォントがあります' },
    cause: {
      en: 'The page names a font and the file does not contain it, so every reader substitutes something else. The characters themselves still read correctly — they come from the content stream, not the font — but the widths do not, and text placed to a substituted font\'s widths drifts away from the marks beneath it.',
      ja: 'ページが指定しているフォントがファイルに含まれておらず、各リーダーが別のフォントで代替します。文字そのものはコンテンツストリームから取得するため正しく読めますが、字幅は代替フォントのものになり、その字幅で配置した文字は下の図形からずれていきます。',
    },
    recommendation: {
      en: 'Export with fonts embedded. On a Japanese drawing this matters most: the CJK font is the one most likely to be absent on the machine that opens the file.',
      ja: 'フォントを埋め込んで出力してください。日本語図面では特に重要です。開いた環境に存在しない可能性が最も高いのが日本語フォントであるためです。',
    },
    autocad: {
      en: 'Plotter Custom Properties → Font Handling: enable "Capture fonts used in the drawing".',
      ja: 'プロッタの「カスタム プロパティ」→「フォントの処理」で「図面で使用しているフォントを取り込む」を有効にします。',
    },
    solidworks: {
      en: 'Save As → PDF → Options: enable "Embed fonts".',
      ja: '「指定保存」→ PDF →「オプション」で「フォントを埋め込む」を有効にします。',
    },
  },

  non_standard_sheet: {
    title: { en: 'The sheet is not a standard size', ja: '用紙が標準サイズではありません' },
    cause: {
      en: 'The page matches no A-series sheet. Physical scale is read off the sheet, so a page of unrecognised size leaves nothing to read it from — and a drawing converted at the wrong sheet size looks entirely correct while being wrong throughout. An A3 treated as A4 comes out at 71 % of true size.',
      ja: 'ページが A 判のいずれにも一致しません。実寸法は用紙サイズから読み取るため、認識できないサイズでは基準が得られません。用紙サイズを誤ったまま変換した図面は、全体が誤っていながら正しく見えます。A3 を A4 として扱った場合、実寸の 71 % で出力されます。',
    },
    recommendation: {
      en: 'Where the sheet is genuinely non-standard, the drawing scale has to be stated explicitly rather than derived. This is a property of the drawing rather than of the export, so it is reported rather than treated as a fault.',
      ja: '実際に非標準の用紙である場合は、尺度を自動導出せず明示的に指定する必要があります。出力設定ではなく図面自体の性質であるため、不具合ではなく事実として報告します。',
    },
    autocad: null,
    solidworks: null,
  },

  content_outside_page: {
    title: { en: 'Geometry lies outside the page', ja: '用紙の外に図形があります' },
    cause: {
      en: 'The file carries marks beyond the page a reader displays. A plot area smaller than the drawing extents is the usual reason. Those marks are invisible to anyone opening the PDF and would convert into DXF entities that are not on the drawing that was approved.',
      ja: 'リーダーが表示する用紙範囲の外に図形が含まれています。印刷範囲が図面範囲より小さい場合に多く発生します。これらは PDF を開いても見えませんが、変換すると承認された図面に存在しない要素として DXF に現れます。',
    },
    recommendation: {
      en: 'Export with the plot area set to the drawing extents, so that what the file carries and what a reader sees are the same drawing.',
      ja: '印刷範囲を図面範囲に設定して出力し、ファイルの内容と表示内容を一致させてください。',
    },
    autocad: {
      en: 'Plot dialog → Plot area: Extents or Layout, with "Fit to paper" or an explicit 1:1 scale.',
      ja: '印刷ダイアログの「印刷範囲」を「図面範囲」または「レイアウト」に設定し、「用紙に合わせる」または 1:1 の尺度を指定します。',
    },
    solidworks: {
      en: 'Check that the drawing view sits inside the sheet boundary before exporting; content outside the sheet is not plotted but is still written.',
      ja: '出力前に、図面ビューがシート枠内に収まっていることを確認してください。シート外の内容は印刷されませんが、ファイルには書き込まれます。',
    },
  },

  text_write_order: {
    title: { en: 'Where one text entity ends is a producer choice', ja: '文字要素の区切りは出力側の書き方に依存します' },
    cause: {
      en: 'A PDF carries no text entities. One visible line arrives as however many pieces the producer chose to emit, and where one block of text ends is recoverable only from the order those pieces were written in — a producer writes a multi-line note as a unit and a table across its rows. This drawing contains groupings the spacing alone would have merged and the write order kept apart. The reading taken here is the one the evidence supports; a producer that writes its text in geometric sweep order would be read differently, and would still be a conforming PDF.',
      ja: 'PDF には文字要素という概念がありません。1 行に見える文字列は、出力側が分割した数だけの断片として格納され、どこまでが 1 つの文字ブロックかは、それらが書き込まれた順序からしか判断できません。出力側は複数行の注記をひとまとまりとして書き、表は行方向に書きます。本図面には、行間だけを見れば結合されるが書き込み順序により分離された組み合わせが含まれています。ここでの解釈は根拠のある読み方ですが、幾何学的な走査順で文字を書き出す出力ツールでは異なる結果となり、それも規格に適合した PDF です。',
    },
    recommendation: {
      en: 'Every such pair is reported rather than decided silently, and the count is on the record. Where the reassembled TEXT and MTEXT counts matter — they are what the accuracy figure is measured against — the reassembly rule including this write-order condition should be named in the matching specification, so that both sides measure against a stated rule rather than against whichever heuristic each built.',
      ja: '該当する組み合わせはすべて記録し、件数を明示します。再構成後の TEXT・MTEXT の数は精度評価の対象そのものであるため、書き込み順序の条件を含む再構成ルールをマッチング仕様に明記し、双方が同一の規定に基づいて評価できるようにすることを推奨します。',
    },
    autocad: null,
    solidworks: null,
  },
};
